import React, { Component } from "react";
import { Alert, Container, SpaceBetween } from "@amzn/awsui-components-react";
import LinkServiceBackendClient from "../common/LinkServiceBackendClient";
import TrailblazerVisualWrapper from "../trailblazerVisualization/TrailblazerVisualWrapper";
import TabSpinner from "../components/TabSpinner";
import Constants from "../utils/Constants";

/**
 * This component handles the trailblazer visualization for a specific link.
 * It automatically uses the end_a_nsm_name attribute from the link as the search term.
 */
export default class LinkTrailblazer extends Component {
    static SEARCH_STATES = {
        IDLE: "idle",
        SEARCHING: "searching",
        SUCCESS: "success",
        ERROR: "error"
    };

    constructor(props) {
        super(props);
        this.state = {
            searchState: LinkTrailblazer.SEARCH_STATES.IDLE,
            trailblazerOutput: null,
            errorMessage: ""
        };
    }

    componentDidMount = async () => {
        await this.executeSearch();
    }

    componentDidUpdate = async (prevProps) => {
        // If the link changes, re-execute the search
        if (prevProps.link.instanceId !== this.props.link.instanceId) {
            await this.executeSearch();
        }
    }

    getSearchTerm = () => {
        const { link } = this.props;
        // Find the end_a_nsm_name attribute from the link
        const endANsmNameAttr = link.attributes?.find(
            attr => attr.key === Constants.ATTRIBUTES.end_a_nsm_name
        );

        if (endANsmNameAttr) {
            let searchTerm = this.linkServiceBackendClient.getAttributeValue(endANsmNameAttr);

            // Add regex qualifiers for PassiveToPassive link type
            if (link.readableLinkType === Constants.LINK_TYPES.passiveToPassive) {
                searchTerm = `/${searchTerm}/`;
            }

            return searchTerm;
        }

        return null;
    }

    linkServiceBackendClient = LinkServiceBackendClient.getInstance();

    async executeSearch() {
        const searchTerm = this.getSearchTerm();

        if (!searchTerm) {
            this.setState({
                searchState: LinkTrailblazer.SEARCH_STATES.ERROR,
                errorMessage: `No ${Constants.ATTRIBUTES.end_a_nsm_name} attribute found for this link.`,
                trailblazerOutput: null
            });
            return;
        }

        this.setState({
            searchState: LinkTrailblazer.SEARCH_STATES.SEARCHING,
            errorMessage: ""
        });

        try {
            const trailblazerRouteOutput = await this.linkServiceBackendClient.trailblazerRoute(searchTerm);

            this.setState({
                searchState: LinkTrailblazer.SEARCH_STATES.SUCCESS,
                trailblazerOutput: trailblazerRouteOutput
            });
        } catch (error) {
            this.setState({
                searchState: LinkTrailblazer.SEARCH_STATES.ERROR,
                errorMessage: error.message,
                trailblazerOutput: null
            });
        }
    }

    renderContent() {
        const { searchState, trailblazerOutput, errorMessage } = this.state;

        switch (searchState) {
        case LinkTrailblazer.SEARCH_STATES.SEARCHING:
            return <TabSpinner/>;
        case LinkTrailblazer.SEARCH_STATES.ERROR:
            return (
                <Container>
                    <Alert type="error" header="Error loading Trailblazer visualization">
                        {errorMessage}
                    </Alert>
                </Container>
            );
        case LinkTrailblazer.SEARCH_STATES.SUCCESS:
            return trailblazerOutput ?
                <TrailblazerVisualWrapper trailblazerOutput={trailblazerOutput}/> :
                <Container>
                    <Alert type="warning">
                        LinkService client produced invalid Trailblazer API output
                    </Alert>
                </Container>;
        default:
            return <></>;
        }
    }

    render() {
        const searchTerm = this.getSearchTerm();

        return (
            <SpaceBetween size={Constants.PADDING_SIZES.SPACE_BETWEEN_SECTIONS}>
                {searchTerm && (
                    <Alert statusIconAriaLabel="Info" header="Trailblazer Visualization">
                        Showing route visualization for: <strong>{searchTerm}</strong>
                    </Alert>
                )}
                {this.renderContent()}
            </SpaceBetween>
        );
    }
}
